import axios from 'axios';
import { notifyUnauthorized } from './authEvents';

// Connecting a Google account, the meetings and email headers it brings in, and the candidate clients they turn up
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || '',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' }
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) notifyUnauthorized();
    return Promise.reject(error);
  }
);

const call = async (request, fallbackMessage) => {
  try {
    return (await request()).data;
  } catch (error) {
    const detail = error.response?.data?.detail;
    throw new Error(typeof detail === 'string' ? detail : fallbackMessage);
  }
};

const googleApi = {
  /** { configured, missing, notice, can_connect, sync_from, connection } for the signed-in person */
  mine: () => call(() => apiClient.get('/api/v2/me/google'), "Couldn't load your Google account"),

  /** The Google URL to send the browser to; `acknowledged` is the person agreeing to what colleagues will see */
  connect: () => call(
    () => apiClient.post('/api/v2/me/google/connect', { acknowledged: true }), "Couldn't start connecting Google"),

  disconnect: () => call(() => apiClient.delete('/api/v2/me/google'), "Couldn't disconnect Google"),

  /** Admins: { connections, configured, missing, shared_account } */
  connections: () => call(() => apiClient.get('/api/v2/admin/google-connections'), "Couldn't load connections"),

  connectShared: () => call(
    () => apiClient.post('/api/v2/admin/google-connections/shared/connect'), "Couldn't start connecting the shared account"),

  adminDisconnect: (id) => call(
    () => apiClient.delete(`/api/v2/admin/google-connections/${id}`), "Couldn't disconnect that account"),

  /** { summary: { total, since, last_met, next }, meetings, has_more } */
  meetings: (companyId, beforeId) => call(
    () => apiClient.get(`/api/v2/companies/${companyId}/meetings`, { params: beforeId ? { before_id: beforeId } : {} }),
    "Couldn't load meetings"),

  /** { summary: { threads, since, last_emailed }, threads, has_more, next_before }; `before` is the last page's next_before */
  emails: (companyId, before) => call(
    () => apiClient.get(`/api/v2/companies/${companyId}/emails`, { params: before ? { before } : {} }),
    "Couldn't load emails"),

  /** Tileward people who can be on a meeting: { colleagues: [{ id, name, email }] } */
  colleagues: () => call(() => apiClient.get('/api/v2/meetings/colleagues'), "Couldn't load colleagues"),

  /** Log a meeting that has no calendar event: { title, start_at, end_at, notes_url, user_ids, contact_ids } */
  logMeeting: (companyId, fields) => call(
    () => apiClient.post(`/api/v2/companies/${companyId}/meetings`, fields), "Couldn't log that meeting"),

  updateMeeting: (id, fields) => call(
    () => apiClient.patch(`/api/v2/meetings/${id}`, fields), "Couldn't change that meeting"),

  deleteMeeting: (id) => call(() => apiClient.delete(`/api/v2/meetings/${id}`), "Couldn't remove that meeting"),

  /** { candidates: [{ key, kind, meetings, latest_at, latest_title, tileward, attendees }] } */
  candidates: () => call(() => apiClient.get('/api/v2/admin/candidate-clients'), "Couldn't load candidate clients"),

  addCandidate: (key, { name, contact_emails }) => call(
    () => apiClient.post(`/api/v2/admin/candidate-clients/${encodeURIComponent(key)}/add`, { name, contact_emails }),
    "Couldn't add that company"),

  dismissCandidate: (key) => call(
    () => apiClient.post(`/api/v2/admin/candidate-clients/${encodeURIComponent(key)}/dismiss`),
    "Couldn't rule that out")
};

export default googleApi;
